/**
 * @file AppSelect.tsx
 * @brief Standardized dropdown select used across the application.
 * @author Nestbotics
 * @date 2026-06-19
 */

import React, { forwardRef } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  type SelectProps,
  type FormControlProps,
  type MenuProps,
  type MenuItemProps,
} from '@mui/material';

/** Maximum height for dropdown menus — keeps long lists scrollable instead of overflowing. */
export const SELECT_MENU_MAX_HEIGHT = 280;

/**
 * @brief Shared MenuProps for all application dropdowns.
 */
export const STANDARD_SELECT_MENU_PROPS: Partial<MenuProps> = {
  PaperProps: {
    sx: {
      maxHeight: SELECT_MENU_MAX_HEIGHT,
      borderRadius: 1,
      boxShadow: '0 4px 16px rgba(15, 23, 42, 0.12)',
    },
  },
  MenuListProps: {
    dense: true,
    sx: { py: 0.5 },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

/** Consistent menu item sizing for all dropdown options. */
export const standardMenuItemSx = {
  minHeight: 36,
  fontSize: '0.875rem',
  py: 0.75,
};

/**
 * @brief Standard menu item for use inside AppSelect.
 */
export const AppMenuItem = forwardRef<HTMLLIElement, MenuItemProps>(function AppMenuItem(
  { sx, ...props },
  ref
) {
  return <MenuItem ref={ref} dense sx={{ ...standardMenuItemSx, ...sx }} {...props} />;
});

export type AppSelectProps = Omit<SelectProps, 'label'> & {
  /** Field label; wraps the select in FormControl + InputLabel when provided. */
  label?: string;
  /** Optional helper text shown below the select. */
  helperText?: string;
  /** Props forwarded to the outer FormControl when label is set. */
  formControlProps?: FormControlProps;
  /** When true, menu width follows option content instead of stretching to the field width. */
  autoWidth?: boolean;
};

/**
 * @brief Application-standard Select with capped menu height and consistent sizing.
 * @param props Standard MUI Select props plus optional label and helperText.
 * @return Rendered select, optionally wrapped in FormControl.
 */
export const AppSelect = forwardRef<unknown, AppSelectProps>(function AppSelect(
  {
    label,
    helperText,
    error,
    fullWidth = true,
    autoWidth = !fullWidth,
    size = 'small',
    children,
    formControlProps,
    id,
    MenuProps,
    sx,
    ...selectProps
  },
  ref
) {
  const selectId =
    id || (label ? `app-select-${label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}` : undefined);
  const selectedValue = selectProps.value;
  const shrinkEmptyLabel = Boolean(
    selectProps.displayEmpty &&
      label &&
      (selectedValue === '' || selectedValue == null)
  );

  const mergedMenuProps: Partial<MenuProps> = {
    ...STANDARD_SELECT_MENU_PROPS,
    ...MenuProps,
    PaperProps: {
      ...STANDARD_SELECT_MENU_PROPS.PaperProps,
      ...MenuProps?.PaperProps,
      sx: {
        ...(STANDARD_SELECT_MENU_PROPS.PaperProps as { sx?: object })?.sx,
        ...(MenuProps?.PaperProps as { sx?: object })?.sx,
      },
    },
    MenuListProps: {
      ...STANDARD_SELECT_MENU_PROPS.MenuListProps,
      ...MenuProps?.MenuListProps,
      sx: {
        ...(STANDARD_SELECT_MENU_PROPS.MenuListProps as { sx?: object })?.sx,
        ...(MenuProps?.MenuListProps as { sx?: object })?.sx,
      },
    },
  };

  if (!label) {
    return (
      <Select
        ref={ref}
        id={selectId}
        size={size}
        error={error}
        fullWidth={fullWidth}
        autoWidth={autoWidth}
        MenuProps={mergedMenuProps}
        sx={sx}
        {...selectProps}
      >
        {children}
      </Select>
    );
  }

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={error}
      {...formControlProps}
    >
      <InputLabel id={selectId ? `${selectId}-label` : undefined} shrink={shrinkEmptyLabel || undefined}>
        {label}
      </InputLabel>
      <Select
        ref={ref}
        id={selectId}
        label={label}
        labelId={selectId ? `${selectId}-label` : undefined}
        notched={shrinkEmptyLabel || undefined}
        size={size}
        error={error}
        fullWidth={fullWidth}
        autoWidth={autoWidth}
        MenuProps={mergedMenuProps}
        sx={sx}
        {...selectProps}
      >
        {children}
      </Select>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
});

export default AppSelect;
